<script setup lang="ts">
import type { Link } from '@unhead/vue';
import { safeParse as vSafeParse } from 'valibot';
import type { HeadHrefLangLink } from '#shared/url';
import { languages } from '#shared/application';
import { getTranslatedSerpSlug } from '#shared/property/url-serp';
import type { PropertySearchCriteria } from '#shared/property/search-criteria';
import { type IadAppRoute, ErrorSchema } from '~/application';
import { useCloneOnce } from '~/composables/editable-clone-once';

definePageMeta({
  layout: 'sticky',
  pageTemplate: 'product_listing',
  pageType: 'product_listing',
  middleware: [
    `handle-legacy-business-slug`,
  ],
});

const route = useRoute();
const serpSlug = toRef(() => route.params.serpSlug);
const query = toRef(route, `query`);
const { data: properties, suspense, isError, error: propertiesError } = useSearchProperties(serpSlug, query);

const page = useQueryParamNumber('page', 1);

// https://tanstack.com/query/latest/docs/framework/vue/guides/suspense
await suspense();

// const errorResult = vSafeParse(ErrorSchema, propertiesError.value);
const errorStatusCode = computed(() => {
  if (!isError.value) return undefined;
  const errorResult = vSafeParse(ErrorSchema, propertiesError.value);
  if (!errorResult.success) return 500;
  return errorResult.output.statusCode;
});

// Since Serp Slug can't be translated automatically by Nuxt I18n
// We need to generate the alternate links manually
const slugs = computed(() => getTranslatedSerpSlug(serpSlug.value));
const localePath = useLocalePath();
const config = useRuntimeConfig();
const { locale, defaultLocale } = useI18n();

// This is a quick and dirty array comparison
// We could do better with stuff like
// • https://www.npmjs.com/package/array-equality
// But it will be one more dependency & we are just comparing array of strings once on the page
const hasOnlyUnderstoodSlugs = JSON.stringify(serpSlug.value) === JSON.stringify(properties.value?.serpSlug);
const shouldRedirectToCleanSlug = hasOnlyUnderstoodSlugs === false && !isError.value;
if (shouldRedirectToCleanSlug) {
  const pathParams: IadAppRoute<'serp-serpSlug'> = {
    name: 'serp-serpSlug',
    params: { serpSlug: properties.value?.serpSlug },
    query: route.query,
  };
  const path = localePath(pathParams, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}

const headLinks = computed(() => {
  // Stop computing to helps tests: avoid calls of localePath()
  if (shouldRedirectToCleanSlug) return [];
  const links: Array<HeadHrefLangLink> = languages.map((lang) => {
    const routeParams = {
      serpSlug: slugs.value[lang],
    };
    const pathParams: IadAppRoute<'serp-serpSlug'> = {
      name: 'serp-serpSlug',
      params: routeParams,
      query: {
        page: page.value > 1 ? page.value : undefined,
      },
    };
    const href = localePath(pathParams, lang);
    return {
      id: `i18n-alt-${lang}`,
      rel: 'alternate',
      // make an absolute URL
      href: new URL(href, config.public.i18n.baseUrl).href,
      hreflang: lang,
    };
  });
  const defaultAlternate = links.find(link => link.hreflang === defaultLocale);
  if (defaultAlternate) {
    const link = { ...defaultAlternate };
    link.hreflang = 'x-default';
    link.id = 'i18n-xd';
    links.push(link);
  };
  const canonical = links.find(link => link.hreflang === locale.value);
  if (canonical) {
    const link = { ...canonical };
    link.rel = 'canonical';
    link.id = 'i18n-can';
    delete link.hreflang;
    links.push(link);
  };
  return links;
});

const i18nHead = useLocaleHead({ key: 'id' });
useHead({
  title: properties.value?.seo.headTitle,
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  // Lie to TS as the format follow what is returned by NuxtI18n
  link: [...(headLinks.value as Link[] || [])],
  meta: [
    ...(i18nHead.value?.meta ?? []),
    {
      name: 'description',
      content: properties.value?.seo.metaDescription,
    },
  ],
});

const withoutItems = computed(() => properties.value?.items.length === 0);
const isSortable = computed(() => (properties.value?.items.length ?? 0) > 1);

const isFilterModalOpen = ref(false);
const isAlertModalOpen = ref(false);

const { propertySearchCriteria: routePropertySearchCriteria } = usePropertySearchCriteria(route);
const { modelValue: propertySearchCriteria } = useCloneOnce(routePropertySearchCriteria);

function onAlert(alertPropertySearchCriteria: PropertySearchCriteria) {
  propertySearchCriteria.value = alertPropertySearchCriteria;
  isAlertModalOpen.value = true;
}
</script>

<template>
  <div class="contents">
    <IadWrapper
      :padded="false"
      inner-classes="pb-m"
    >
      <PropertyEditorializedHeader />
    </IadWrapper>
    <IadWrapper
      v-if="properties"
      inner-classes="pb-s"
    >
      <IadTitle
        tag="h1"
        variant="large"
        :text="properties.seo.h1"
      />
    </IadWrapper>
    <PropertyFilterBar
      v-model:open="isFilterModalOpen"
      :main-location="properties?.mainLocation"
      @alert="onAlert"
    />
    <IadPlaceholderError
      v-if="errorStatusCode"
      :status-code="errorStatusCode"
    />
    <IadWrapper :padded="!withoutItems">
      <PropertySort
        v-if="isSortable"
        class="w-40 hidden lg:block"
      />
      <PropertyEmpty
        v-if="withoutItems"
        @alert="onAlert"
      />
      <template v-if="properties">
        <PropertyGrid
          :properties="properties.items"
          @alert="onAlert"
        />
        <PropertySerpSimilar
          v-if="properties.similarItems.length"
          :properties="properties.similarItems"
        />
        <IadPagination
          title-translation-key="property.common.pagination"
          :items-per-page="properties.itemsPerPage"
          :total-items="properties.totalItems"
          :page
        />
        <PropertySeoAgentBlock
          v-if="properties.mainLocation"
          :location="properties.mainLocation"
          class="mt-l"
        />
        <PropertySeoColumns
          :seo-links="properties.seoLinks"
          class="mt-l"
        />
        <IadBreadcrumbs
          :items="properties.breadcrumbs"
          class="py-2xl"
        />
      </template>
      <ClientOnly>
        <PropertyDialogFilter
          v-model="isFilterModalOpen"
          @alert="onAlert"
        />
        <AlertDialog
          v-model="isAlertModalOpen"
          :property-search-criteria
        />
      </ClientOnly>
    </IadWrapper>
    <AlertStickyCta v-model="isAlertModalOpen" />
  </div>
</template>
